/**
 * Email Account Store — Wallet-scoped credential persistence via IndexedDB
 * 
 * Replaces the fragile localStorage-based getEmailAccounts() / saveEmailAccounts()
 * pattern with async IndexedDB operations through MobyDB.emailAccounts table.
 * 
 * Key design decisions:
 *   - Async API (IndexedDB is async; localStorage was sync)
 *   - One-time migration from localStorage on first read after upgrade
 *   - EmailAccount interface (boolean enabled) ↔ EmailAccountEntity (number enabled)
 *     conversion handled transparently
 *   - Consumers call getEmailAccountsFromDB(db) instead of getEmailAccounts()
 */

import type { MobyDB, EmailAccountEntity } from "@/moby/db";
import type { EmailAccount } from "@/components/email-drawer";

// ═══════════════════════════════════════════════════════════════════════════════
// CONVERSION: EmailAccount (UI, boolean enabled) ↔ EmailAccountEntity (DB, number)
// ═══════════════════════════════════════════════════════════════════════════════

function entityToAccount(entity: EmailAccountEntity): EmailAccount {
  return {
    id: entity.id,
    email: entity.email,
    imapHost: entity.imapHost,
    imapPort: entity.imapPort,
    smtpHost: entity.smtpHost,
    smtpPort: entity.smtpPort,
    username: entity.username,
    password: entity.password,
    color: entity.color,
    enabled: entity.enabled === 1,
    authType: entity.authType,
    accessToken: entity.accessToken,
    refreshToken: entity.refreshToken,
    tokenExpiry: entity.tokenExpiry,
    registeredWithMobyChat: entity.registeredWithMobyChat === 1,
    isPrimary: entity.isPrimary === 1,
  };
}

// Concrete type matching EmailAccountEntity fields for Dexie insert/put operations.
// Cannot use Omit<EmailAccountEntity, ...> because Dexie Entity base class properties
// cause the generic Omit to collapse to an empty type.
interface EmailAccountRow {
  id: string;
  email: string;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  color: string;
  enabled: number;
  authType: 'password' | 'oauth2';
  accessToken?: string;
  refreshToken?: string;
  tokenExpiry?: number;
  registeredWithMobyChat?: number;
  isPrimary?: number;
  createdAt: number;
  updatedAt: number;
}

function accountToEntity(account: EmailAccount): EmailAccountRow {
  const now = Date.now();
  return {
    id: account.id,
    email: account.email,
    imapHost: account.imapHost,
    imapPort: account.imapPort,
    smtpHost: account.smtpHost,
    smtpPort: account.smtpPort,
    username: account.username,
    password: account.password || '',
    color: account.color,
    enabled: account.enabled ? 1 : 0,
    authType: account.authType || 'password',
    accessToken: account.accessToken,
    refreshToken: account.refreshToken,
    tokenExpiry: account.tokenExpiry,
    registeredWithMobyChat: account.registeredWithMobyChat ? 1 : 0,
    isPrimary: account.isPrimary ? 1 : 0,
    createdAt: now,
    updatedAt: now,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// MIGRATION: One-time localStorage → IndexedDB
// ═══════════════════════════════════════════════════════════════════════════════

const migrationAttempted = new WeakSet<MobyDB>();

/**
 * Migrate email accounts from localStorage to IndexedDB (one-time).
 * Runs automatically on first getEmailAccountsFromDB() call per DB instance.
 * After successful migration, localStorage copy is cleared.
 */
async function migrateFromLocalStorage(db: MobyDB): Promise<void> {
  if (migrationAttempted.has(db)) return;
  migrationAttempted.add(db);

  try {
    // Check if DB already has accounts (migration already done)
    const existingCount = await db.emailAccounts.count();
    if (existingCount > 0) {
      console.debug('[EmailAccountStore] DB already has accounts, skipping migration');
      return;
    }

    // Read from localStorage
    const stored = localStorage.getItem('moby_pref_emailAccounts');
    if (!stored) return;

    const localAccounts: EmailAccount[] = JSON.parse(stored);
    if (!Array.isArray(localAccounts) || localAccounts.length === 0) return;

    console.log(`[EmailAccountStore] Migrating ${localAccounts.length} accounts from localStorage → IndexedDB`);

    // Write to IndexedDB
    const entities = localAccounts.map(a => ({
      ...accountToEntity({
        ...a,
        authType: a.authType || 'password', // Migrate older accounts
      }),
    }));

    await db.emailAccounts.bulkPut(entities);

    // Clear localStorage copy after successful migration
    localStorage.removeItem('moby_pref_emailAccounts');
    console.log('[EmailAccountStore] Migration complete, localStorage cleared');
  } catch (err) {
    console.warn('[EmailAccountStore] Migration failed (will retry next load):', err);
    // Don't clear localStorage on failure — retry next time
    migrationAttempted.delete(db);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TOKEN REFRESH
// ═══════════════════════════════════════════════════════════════════════════════

/** Result of a token refresh attempt */
export type TokenRefreshResult =
  | { status: 'fresh' }                                      // token still valid
  | { status: 'refreshed'; accessToken: string; tokenExpiry: number }
  | { status: 'revoked'; error: string }                     // refresh token revoked — needs re-auth
  | { status: 'error'; error: string };                      // transient failure — retry later

// 5-minute buffer: refresh early to avoid races between expiry check and IMAP connect
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/**
 * Refresh an OAuth2 access token if it is expired (or about to expire).
 * On success, persists the new token to IndexedDB and returns it.
 * Callers should update their in-memory account copy with the returned values.
 */
export async function refreshOAuth2TokenIfNeeded(
  db: MobyDB,
  account: EmailAccount,
  gatewayUrl: string,
): Promise<TokenRefreshResult> {
  // Not an OAuth2 account — nothing to do
  if (account.authType !== 'oauth2' || !account.accessToken) {
    return { status: 'fresh' };
  }

  // Token still valid (with buffer)
  if (account.tokenExpiry && Date.now() < account.tokenExpiry - TOKEN_EXPIRY_BUFFER_MS) {
    return { status: 'fresh' };
  }

  // No refresh token — can't refresh, need re-auth
  if (!account.refreshToken) {
    return { status: 'revoked', error: 'No refresh token — please re-authenticate with Google' };
  }

  console.log(`[TokenRefresh] Token expired for ${account.email}, attempting refresh...`);

  try {
    const res = await fetch(`${gatewayUrl}/api/email/oauth/google/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: account.refreshToken }),
    });

    const data = await res.json();

    if (data.success && data.accessToken) {
      const newExpiry = Date.now() + (data.expiresIn * 1000);

      // Persist to IndexedDB
      try {
        const entity = await db.emailAccounts.get(account.id);
        if (entity) {
          await db.emailAccounts.update(account.id, {
            accessToken: data.accessToken,
            tokenExpiry: newExpiry,
            updatedAt: Date.now(),
          });
        }
      } catch (dbErr) {
        console.warn('[TokenRefresh] Failed to persist refreshed token to DB:', dbErr);
      }

      // Backward-compat: sync to localStorage
      try {
        const stored = localStorage.getItem('moby_pref_emailAccounts');
        if (stored) {
          const allAccounts = JSON.parse(stored);
          const idx = allAccounts.findIndex((a: any) => a.id === account.id);
          if (idx >= 0) {
            allAccounts[idx].accessToken = data.accessToken;
            allAccounts[idx].tokenExpiry = newExpiry;
            localStorage.setItem('moby_pref_emailAccounts', JSON.stringify(allAccounts));
          }
        }
      } catch { /* non-fatal */ }

      console.log(`[TokenRefresh] ✓ Token refreshed for ${account.email}`);
      return { status: 'refreshed', accessToken: data.accessToken, tokenExpiry: newExpiry };
    }

    // Detect revocation: Google returns "invalid_grant" when refresh token is revoked
    const errMsg: string = data.error || 'Token refresh failed';
    if (errMsg.includes('invalid_grant') || errMsg.includes('Token has been revoked')) {
      console.warn(`[TokenRefresh] Token REVOKED for ${account.email}:`, errMsg);
      return { status: 'revoked', error: errMsg };
    }

    console.warn(`[TokenRefresh] Refresh failed for ${account.email}:`, errMsg);
    return { status: 'error', error: errMsg };
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : 'Network error during token refresh';
    console.warn(`[TokenRefresh] Network error for ${account.email}:`, errMsg);
    return { status: 'error', error: errMsg };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get all email accounts from IndexedDB (wallet-scoped).
 * Triggers one-time localStorage migration if needed.
 */
export async function getEmailAccountsFromDB(db: MobyDB): Promise<EmailAccount[]> {
  await migrateFromLocalStorage(db);

  try {
    const entities = await db.emailAccounts.toArray();
    const accounts = entities.map(entityToAccount);
    // Keep localStorage in sync so backward-compat consumers and fallback reads
    // always have data (migration may have cleared it on a previous load).
    if (accounts.length > 0) {
      try {
        localStorage.setItem('moby_pref_emailAccounts', JSON.stringify(accounts));
      } catch { /* best-effort sync */ }
    }
    return accounts;
  } catch (err) {
    console.error('[EmailAccountStore] Failed to read accounts from DB:', err);
    // Fallback to localStorage for graceful degradation
    return getEmailAccountsFromLocalStorage();
  }
}

/**
 * Save (upsert) a single email account to IndexedDB.
 * Also writes to localStorage as a backward-compat fallback until all
 * consumers are migrated.
 */
export async function saveEmailAccountToDB(db: MobyDB, account: EmailAccount): Promise<void> {
  try {
    const entity = accountToEntity(account);
    // Check if this is an update (preserve createdAt)
    const existing = await db.emailAccounts.get(account.id);
    if (existing) {
      entity.createdAt = existing.createdAt;
    }
    entity.updatedAt = Date.now();
    await db.emailAccounts.put(entity);
    console.debug('[EmailAccountStore] Account saved to DB:', account.email);
  } catch (err) {
    console.error('[EmailAccountStore] Failed to save account to DB:', err);
  }

  // Backward-compat: also update localStorage for components not yet migrated
  syncToLocalStorage(db);
}

/**
 * Save all email accounts to IndexedDB (bulk replace).
 * Used by email-drawer saveAccount/deleteAccount which operate on the full list.
 */
export async function saveAllEmailAccountsToDB(db: MobyDB, accounts: EmailAccount[]): Promise<void> {
  try {
    await db.transaction('rw', db.emailAccounts, async () => {
      // Clear existing and replace with new list
      await db.emailAccounts.clear();
      const entities = accounts.map(a => accountToEntity(a));
      await db.emailAccounts.bulkPut(entities);
    });
    console.debug(`[EmailAccountStore] ${accounts.length} accounts saved to DB`);
  } catch (err) {
    console.error('[EmailAccountStore] Failed to save accounts to DB:', err);
  }

  // Backward-compat: also update localStorage for components not yet migrated
  syncToLocalStorage(db);
}

/**
 * Delete a single email account from IndexedDB.
 */
export async function deleteEmailAccountFromDB(db: MobyDB, accountId: string): Promise<void> {
  try {
    await db.emailAccounts.delete(accountId);
    console.debug('[EmailAccountStore] Account deleted from DB:', accountId);
  } catch (err) {
    console.error('[EmailAccountStore] Failed to delete account from DB:', err);
  }

  syncToLocalStorage(db);
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKWARD COMPAT: Keep localStorage in sync until all consumers are migrated
// ═══════════════════════════════════════════════════════════════════════════════

async function syncToLocalStorage(db: MobyDB): Promise<void> {
  try {
    const accounts = await getEmailAccountsFromDB(db);
    localStorage.setItem('moby_pref_emailAccounts', JSON.stringify(accounts));
    window.dispatchEvent(new CustomEvent('user-preferences-updated'));
  } catch {
    // Best-effort sync
  }
}

/**
 * Legacy localStorage read — used as fallback when IndexedDB fails.
 */
function getEmailAccountsFromLocalStorage(): EmailAccount[] {
  try {
    const stored = localStorage.getItem('moby_pref_emailAccounts');
    if (stored) {
      const accounts = JSON.parse(stored) as EmailAccount[];
      return accounts.map(account => ({
        ...account,
        authType: account.authType || 'password',
      }));
    }
  } catch {
    // ignore
  }
  return [];
}
